import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, ListItem, StepConnector, TextField, useMediaQuery, useTheme, ListItemIcon, ListItemText, Step, StepLabel, Stepper, Alert, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {alertActions} from "../../../store/actions/alertActions";
import useCustomStyles from "../../utils/UseCustomStyles";

const customStyles = (theme) => ({
    chooseAccount: {
        fontWeight: 500,
        fontSize: 14,
        paddingTop: 8,
        paddingBottom: 4
    },
    listItem: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: 0
    },
    listItemIcon: {
        minWidth: 42
    },
    stepper: {
        margin: "0 auto",
        width: "100%",
        maxWidth: 800,
        padding: "0px 24px 24px 24px"
    },
    stepConnectorLine: {
        borderColor: theme.palette.background.tableBorder
    },
});

function OutbrainIntegration(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {integration, resetIntegration, create, update, find, alert, getAdAccounts, removeIntegrationEvent} = props;

    const steps = ["Connect", 'Integrate Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [step1Data, setStep1Data] = useState(null);
    const [step2Data, setStep2Data] = useState(null);

    const {register, handleSubmit, control, setValue, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    const handleOnSubmitStep1 = (data) => {
        setStep1Data(data);
        setActiveStep(2);
    };

    const handleOnIntegrateAdAccount = (data) => {
        setStep2Data(data);
    };

    const handleOnSubmitStep2 = () => {
        setActiveStep(3);
    };

    const handleOnSubmit = (data) => {
        if (integrationId > 0) {
            update({
                integrationId: integrationId,
                name: step1Data?.name,
                apiToken: step1Data?.apiToken,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        } else {
            create({
                name: step1Data?.name,
                apiToken: step1Data?.apiToken,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        }
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integrationId > 0) {
            find(integrationId);
        }
    }, [find, integrationId]);

    useEffect(() => {
        if (activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "outbrain_api_token") {
                        setValue("apiToken", cred?.keyValue);
                    }
                    if (cred?.keyName === "outbrain_account_currency") {
                        setValue("currency", cred?.keyValue);
                    }
                }
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Set step 1 form data.
    useEffect(() => {
        if (activeStep === 1 && step1Data !== null) {
            setValue("name", step1Data?.name);
            setValue("apiToken", step1Data?.apiToken);
        }
    }, [activeStep, setValue, step1Data]);

    // Load Outbrain ad accounts if step 2.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts === null) {
            getAdAccounts({
                apiToken: step1Data?.apiToken,
                integrationId: integrationId
            });
        }
    }, [activeStep, getAdAccounts, step1Data?.apiToken, integration?.adAccounts, integrationId]);

    // If integrated then set step 2 data.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts !== null) {
            integration?.adAccounts.forEach((item) => {
                if (item?.integrated) {
                    setStep2Data({
                        adAccountId: item?.adAccountId,
                        adAccountName: item?.adAccountName,
                        currency: item?.currency
                    });
                }
            });
        }
    }, [activeStep, integration?.adAccounts])

    useEffect(() => {
        if (activeStep === 3 && integration?.currentIntegration !== null && getValues("events")?.length === 0) {
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "outbrain_enable_conversion_api") {
                    if (cred?.keyValue === "true") {
                        setValue("enableConversionAPI", true);
                    } else {
                        setValue("enableConversionAPI", false);
                    }
                }
            }
            let events = [];
            integration?.currentIntegration?.integrationEvents?.forEach((event) => {
                events.push({
                    eventId: event?.id,
                    name: event?.name,
                    includePayout: event?.includePayout
                });
            });
            setValue("events", events);
        }
    }, [activeStep, setValue, integration?.currentIntegration, getValues]);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg, handleClose]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="outbrain-account-integration"
        >
            <IntegrationHeader name="Outbrain" logo="/assets/images/traffic-sources/outbrain.png" id="outbrain-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep - 1}
                    connector={<StepConnector classes={{line: classes?.stepConnectorLine}}/>}
                    classes={{root: classes?.stepper}}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <List>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Check the status of your campaign, ads and sections."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Change daily budget for campaigns."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Change the bid value for campaign and sections."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Pause or resume campaign, ads, publishers and sections."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Sync cost on campaign level and section level every 30 min."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Check delivery status of campaigns."/>
                            </ListItem>
                        </List>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/outbrain-integration">here</Link>.
                        </p>

                        {integration?.loading?.integration && <Spinner overComponentBox={true}/>}
                        <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box display="flex" flexWrap={"wrap"}>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        id="name"
                                        label="Integration Name*"
                                        {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                        name="name"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your integration name"
                                        fullWidth={true}
                                        defaultValue="Outbrain"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.apiToken}
                                        variant="outlined"
                                        margin="dense"
                                        id="apiToken"
                                        label="API Token*"
                                        {...register("apiToken", {required: true, maxLength: 1024, minLength: 2})}
                                        name="apiToken"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your API Token"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </form>
                    </>
                }

                {activeStep === 2 &&
                    <Box mb={2}>
                        <Box mb={2} style={{fontWeight: 600}}>
                            <Alert variant="filled" severity="info">
                                Choose the Outbrain account you want to integrate. If you have multiple accounts then
                                you need to integrate them separately.
                            </Alert>
                        </Box>
                        <div className={classes?.chooseAccount}>Outbrain Ad Accounts</div>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="Outbrain">
                                    {integration?.adAccounts?.map((item) => {
                                        return (
                                            <IntegrationAdAccountRow key={item?.adAccountId} name={item?.adAccountName || item?.name}
                                                accountId={item?.adAccountId} integrated={item?.adAccountId === step2Data?.adAccountId}
                                                onIntegrate={() => handleOnIntegrateAdAccount({
                                                            adAccountId: item?.adAccountId,
                                                            adAccountName: item?.adAccountName,
                                                            currency: item?.currency
                                                        })}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </Box>
                }

                {activeStep === 3 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="Outbrain" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmitStep2)} color="primary" variant="contained">
                                    Next
                                </Button>
                            </Box>
                        </>
                    }
                    {activeStep === 3 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(2)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    getAdAccounts: integrationActions.getOutbrainAdAccounts,
    create: integrationActions.createOutbrainIntegration,
    update: integrationActions.updateOutbrainIntegration,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(OutbrainIntegration);
